import { IBE_BASE_URL } from '../constants/config';
import { getVisitorId } from './brevoVisitorId';

// Port of travelopod-web-cms's src/lib/brevo/browser.ts history buffer.
//
// Pre-lead browsing events (search, results view, result selected, phone-CTA
// click) are NOT sent to Brevo as they happen: every /v3/events call creates a
// Brevo contact for an unknown ext_id, which turned every anonymous visitor
// into a UUID-only contact. They are validated by ibe-api (the same schemas
// the CMS uses), buffered here, and handed over with the booking request —
// ibe-api creates the real contact first, then replays this history onto it
// with the original timestamps. Once this browser has booked, later events go
// straight through POST /events/brevo-events instead.

const BREVO_HISTORY_KEY = 'brevo_history';
const BREVO_IDENTIFIED_KEY = 'brevo_identified';
const BREVO_UTM_PARAMS_KEY = 'brevo_utm_params';
const BREVO_HISTORY_MAX_EVENTS = 50;
const BREVO_HISTORY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
// Browsers reject a keepalive request whose body is over 64KB.
const BREVO_HISTORY_MAX_BYTES = 48000;

const UTM_QUERY_TO_CAMEL = {
  utm_device: 'utmDevice',
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
};

// First-touch UTM attribution, persisted once from whichever URL first carries
// UTM params and reused for every event afterwards — most events fire deep in
// the funnel, where the URL may no longer carry the original campaign params.
export const getUtmParams = () => {
  try {
    const stored = localStorage.getItem(BREVO_UTM_PARAMS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }

    const searchParams = new URLSearchParams(window.location.search);
    const captured = {};
    Object.entries(UTM_QUERY_TO_CAMEL).forEach(([queryKey, camelKey]) => {
      const value = searchParams.get(queryKey);
      if (value) {
        captured[camelKey] = value;
      }
    });

    if (Object.keys(captured).length) {
      localStorage.setItem(BREVO_UTM_PARAMS_KEY, JSON.stringify(captured));
    }

    return captured;
  } catch (e) {
    return {};
  }
};

const readHistory = () => {
  try {
    const raw = localStorage.getItem(BREVO_HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) {
      return [];
    }

    const cutoff = Date.now() - BREVO_HISTORY_MAX_AGE_MS;
    return parsed.filter((entry) => Date.parse(entry?.occurredAt) >= cutoff);
  } catch (e) {
    return [];
  }
};

const writeHistory = (history) => {
  try {
    if (history.length) {
      localStorage.setItem(BREVO_HISTORY_KEY, JSON.stringify(history));
    } else {
      localStorage.removeItem(BREVO_HISTORY_KEY);
    }
  } catch (e) {
    // Storage failures (private browsing, quota) only cost browsing context.
  }
};

const getIdentified = () => {
  try {
    const raw = localStorage.getItem(BREVO_IDENTIFIED_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Removes the buffered history so exactly one request carries it. Pair with
// restoreBrevoHistory if that request doesn't succeed.
export const claimBrevoHistory = () => {
  const history = readHistory();
  writeHistory([]);

  while (history.length && JSON.stringify(history).length > BREVO_HISTORY_MAX_BYTES) {
    history.shift();
  }

  return history;
};

export const restoreBrevoHistory = (history) => {
  if (!history || !history.length) {
    return;
  }

  const merged = [...history, ...readHistory()].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
  writeHistory(merged.slice(-BREVO_HISTORY_MAX_EVENTS));
};

// Sends whatever is buffered straight to Brevo — only for a browser that has
// already booked, so the contact these land on already exists.
const flushBrevoHistory = () => {
  const identified = getIdentified();
  const history = claimBrevoHistory();

  if (!identified || !history.length) {
    restoreBrevoHistory(history);
    return;
  }

  fetch(`${IBE_BASE_URL}/events/brevo-events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // Many events fire right before a tel: hand-off or a navigation, which
    // can cancel an in-flight fetch.
    keepalive: true,
    body: JSON.stringify({
      visitorId: getVisitorId(),
      ...(identified.email ? { email: identified.email } : {}),
      history,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`/events/brevo-events responded ${response.status}`);
      }
    })
    .catch((error) => {
      restoreBrevoHistory(history);
      // eslint-disable-next-line no-console
      console.error('Brevo events request failed:', error);
    });
};

export const markBrevoIdentified = (email) => {
  try {
    const trimmedEmail = email && String(email).trim().toLowerCase();
    localStorage.setItem(BREVO_IDENTIFIED_KEY, JSON.stringify(trimmedEmail ? { email: trimmedEmail } : getIdentified() || {}));
  } catch (e) {
    // Later events just stay buffered.
  }
};

/**
 * Records a pre-lead browsing event. UTM params and page_slug (the full page
 * URL) are captured here, at the moment of the event. Nothing reaches Brevo
 * for an anonymous visitor — it only goes out with the booking (see
 * claimBrevoHistory) — unless this browser has already booked, in which case
 * it is flushed immediately.
 */
export const recordBrevoEvent = (eventName, data) => {
  try {
    const entry = {
      eventName,
      occurredAt: new Date().toISOString(),
      data: { ...data, ...getUtmParams(), pageSlug: window.location.href },
    };
    writeHistory([...readHistory(), entry].slice(-BREVO_HISTORY_MAX_EVENTS));

    if (getIdentified()) {
      flushBrevoHistory();
    }
  } catch (e) {
    // Tracking must never break the page.
  }
};

// The booking request carries the history; these two settle that hand-off
// (mirrors sendBrevoLead in the CMS, adapted to react-refetch where we don't
// own the fetch call). Only one booking request is in flight at a time.
let inFlightHistory = [];

export const claimBrevoHistoryForBooking = () => {
  // A previous attempt that never settled (e.g. tab navigated) is put back first.
  restoreBrevoHistory(inFlightHistory);
  inFlightHistory = claimBrevoHistory();
  return inFlightHistory;
};

export const settleBrevoBooking = ({ success, email }) => {
  const history = inFlightHistory;
  inFlightHistory = [];

  if (success) {
    markBrevoIdentified(email);
  } else {
    restoreBrevoHistory(history);
  }

  // Picks up anything recorded while the booking was in flight, or restored
  // history if the booking failed after an earlier success.
  if (getIdentified()) {
    flushBrevoHistory();
  }
};
